import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { navLinks, company } from '../mock';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur shadow-[0_2px_24px_-12px_rgba(10,37,64,0.25)]' : 'bg-white'
      }`}
    >
      <div className="hidden md:flex items-center justify-between bg-fx-navy text-fx-cream text-xs px-6 lg:px-12 py-2">
        <div className="flex items-center gap-6 opacity-90">
          <span>Canadian equipment finance brokerage</span>
        </div>
        <a href={`tel:${company.phone}`} className="flex items-center gap-2 hover:text-fx-gold transition-colors">
          <Phone className="w-3.5 h-3.5" />
          <span>{company.phone}</span>
        </a>
      </div>

      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-12 h-20">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="relative w-10 h-10 rounded-md bg-fx-navy flex items-center justify-center overflow-hidden">
            <span className="font-display text-fx-gold text-2xl leading-none">F</span>
            <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-fx-gold rounded-sm rotate-12" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display text-fx-navy text-xl font-bold">Foxstone</span>
            <span className="text-[10px] tracking-[0.22em] text-fx-gold font-semibold uppercase">Funding</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navLinks.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === '/'}
              className={({ isActive }) =>
                `fx-nav-link text-sm font-medium tracking-wide text-fx-navy/85 hover:text-fx-navy ${isActive ? 'active text-fx-navy' : ''}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Button asChild className="bg-fx-navy hover:bg-fx-navy-deep text-white rounded-sm px-5">
            <Link to="/contact">
              Contact Us <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2 text-fx-navy" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[360px] p-0">
            <div className="p-6 border-b flex items-center justify-between">
              <span className="font-display text-fx-navy text-xl font-bold">Foxstone Funding</span>
              <button onClick={() => setOpen(false)} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-col p-6 gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === '/'}
                  className={({ isActive }) =>
                    `py-3 px-3 text-base rounded-md transition-colors ${isActive ? 'bg-fx-cream text-fx-navy font-semibold' : 'text-fx-navy/80 hover:bg-fx-cream'}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Button asChild className="mt-4 bg-fx-navy hover:bg-fx-navy-deep text-white">
                <Link to="/contact">Contact Us</Link>
              </Button>
              <a href={`tel:${company.phone}`} className="mt-3 flex items-center justify-center gap-2 text-fx-navy text-sm py-3 border border-fx-navy/15 rounded-md">
                <Phone className="w-4 h-4" /> {company.phone}
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
